import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import swal from 'sweetalert';
import { createInquiry } from '../../api/inquiryApi';
import { lookupPrivateJourney } from '../../api/journeyBookingApi';
import { loadBookableJourneysPage, filterPrivateBookableJourneys } from '../../utils/bookableJourneyLoader';
import {
	fetchBookingHorizon,
	isPrivateDateAllowed,
	privateDateConstraints,
	seasonsLabel,
} from '../../utils/bookingAvailability';
import { formatMoneyLegacy } from '../../utils/currency';

function PrivateTourRequestForm({ embedded = false }) {
	const location = useLocation();
	const currentUser = useSelector((state) => state.userStore?.currentUser) || {};
	const params = new URLSearchParams(location.search);
	const initialJourney = params.get('journey') || '';

	const [journeys, setJourneys] = useState([]);

	useEffect(() => {
		fetchBookingHorizon().then(setBookingHorizon);
		loadBookableJourneysPage({ per_page: 200, private_enabled: true }).then(({ journeys: list }) => {
			setJourneys(filterPrivateBookableJourneys(list));
		});
	}, []);

	const [submitting, setSubmitting] = useState(false);
	const [bookingHorizon, setBookingHorizon] = useState(12);
	const [lookupLoading, setLookupLoading] = useState(false);
	const [preview, setPreview] = useState(null);
	const [form, setForm] = useState({
		journey_slug: initialJourney,
		first_name: '',
		email_address: '',
		phone: '',
		preferred_date: '',
		number_of_adult: 1,
		number_of_child: 0,
		preferred_language: 'English',
		special_requirements: '',
		head_traveller_name: '',
	});

	useEffect(() => {
		if (currentUser?.first_name || currentUser?.email) {
			setForm((f) => ({
				...f,
				first_name: f.first_name || currentUser.first_name || currentUser.name || '',
				email_address: f.email_address || currentUser.email || '',
				phone: f.phone || currentUser.phone || currentUser.contact_number || '',
				head_traveller_name: f.head_traveller_name || currentUser.first_name || currentUser.name || '',
			}));
		}
	}, [currentUser]);

	const selectedJourney = journeys.find((j) => j.slug === form.journey_slug);
	const journeyTitle = selectedJourney?.title?.replace(/\s+Journey$/i, '') || form.journey_slug;
	const dateConstraints = selectedJourney ? privateDateConstraints(selectedJourney, bookingHorizon) : null;
	const totalTravellers = Math.max(1, (Number(form.number_of_adult) || 1) + (Number(form.number_of_child) || 0));

	useEffect(() => {
		if (!form.journey_slug) {
			setPreview(null);
			return;
		}
		setLookupLoading(true);
		lookupPrivateJourney({
			journey_slug: form.journey_slug,
			journey_title: journeyTitle,
			travellers: totalTravellers,
		})
			.then((res) => setPreview(res.data || null))
			.catch(() => setPreview(null))
			.finally(() => setLookupLoading(false));
	}, [form.journey_slug, journeyTitle, totalTravellers]);

	const pricing = preview?.pricing;
	const currencyCode = pricing?.currency_code || 'USD';
	const perPersonDisplay = pricing?.final_price_per_person
		? formatMoneyLegacy(Number(pricing.final_price_per_person), currencyCode)
		: null;
	const totalDisplay = pricing?.total_amount
		? formatMoneyLegacy(Number(pricing.total_amount), currencyCode)
		: null;

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!form.journey_slug) {
			swal('Select a journey', 'Choose which journey you want to travel.', 'warning');
			return;
		}
		if (form.preferred_date && selectedJourney && !isPrivateDateAllowed(form.preferred_date, selectedJourney, bookingHorizon)) {
			swal('Date not available', dateConstraints?.seasonsLabel
				? `Choose a date within the next ${bookingHorizon} months during: ${dateConstraints.seasonsLabel}.`
				: `Choose a date within the next ${bookingHorizon} months.`, 'warning');
			return;
		}
		if (!(currentUser && (currentUser.idx || currentUser.email))) {
			window.location.href = `/login?return=${encodeURIComponent(location.pathname + location.search)}`;
			return;
		}
		setSubmitting(true);
		try {
			const payload = {
				...form,
				journey_title: journeyTitle,
				request_type: 'private_journey',
				travellers: [{ name: form.head_traveller_name || form.first_name }],
				number_of_adult: Number(form.number_of_adult) || 1,
				number_of_child: Number(form.number_of_child) || 0,
			};
			await createInquiry(payload);
			swal(
				'Request received',
				'VisitAllNepal will review your private tour request and contact you with options. Operators are assigned internally — never shown on this site.',
				'success'
			);
			setForm((f) => ({ ...f, special_requirements: '', preferred_date: '' }));
		} catch (err) {
			const msg = err.response?.data?.errors?.[0] || err.response?.data?.errors?.join?.(' ') || 'Could not submit request.';
			swal('Submission failed', msg, 'error');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form className={`ptr-form${embedded ? ' ptr-form--embedded' : ''}`} onSubmit={handleSubmit}>
			<label className="ptr-form__field">
				<span className="ptr-form__label">Journey</span>
				<select
					value={form.journey_slug}
					onChange={(e) => setForm({ ...form, journey_slug: e.target.value })}
					required
				>
					<option value="">Select a journey…</option>
					{journeys.map((j) => (
						<option key={j.slug} value={j.slug}>{j.title.replace(/\s+Journey$/i, '')}</option>
					))}
				</select>
			</label>

			{form.journey_slug && (
				<div className="ptr-form__estimate" role="status" aria-live="polite">
					{lookupLoading ? (
						<p className="ptr-form__estimate-loading">Calculating estimate…</p>
					) : pricing ? (
						<>
							<p className="ptr-form__estimate-route">
								<strong>{preview.journey_title || journeyTitle}</strong>
								{preview.from_to_display && <span>{preview.from_to_display}</span>}
							</p>
							<div className="ptr-form__estimate-price">
								<span className="ptr-form__estimate-label">Estimated total</span>
								<strong>{totalDisplay}</strong>
								<span className="ptr-form__estimate-note">
									{perPersonDisplay}/person × {totalTravellers} traveller{totalTravellers !== 1 ? 's' : ''}
								</span>
								<span className="ptr-form__estimate-fine">
									Indicative only — final quote confirmed by VisitAllNepal after review
								</span>
							</div>
						</>
					) : null}
				</div>
			)}

			<div className="ptr-form__row">
			<label className="ptr-form__field">
				<span className="ptr-form__label">Preferred start date</span>
				<input
					type="date"
					value={form.preferred_date}
					min={dateConstraints?.min}
					max={dateConstraints?.max}
					onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
					required
				/>
				{dateConstraints && (
					<span className="ptr-form__field-hint">
						Book up to {bookingHorizon} months ahead
						{dateConstraints.seasonsLabel !== 'All year' ? ` · Seasons: ${dateConstraints.seasonsLabel}` : ''}
					</span>
				)}
			</label>
			<label className="ptr-form__field">
				<span className="ptr-form__label">Guide language</span>
				<select value={form.preferred_language} onChange={(e) => setForm({ ...form, preferred_language: e.target.value })}>
						<option value="English">English</option>
						<option value="Nepali">Nepali</option>
						<option value="Hindi">Hindi</option>
						<option value="French">French</option>
						<option value="German">German</option>
						<option value="Other">Other</option>
					</select>
				</label>
			</div>

			<div className="ptr-form__row">
				<label className="ptr-form__field">
					<span className="ptr-form__label">Adults</span>
					<input type="number" min="1" value={form.number_of_adult} onChange={(e) => setForm({ ...form, number_of_adult: e.target.value })} />
				</label>
				<label className="ptr-form__field">
					<span className="ptr-form__label">Children</span>
					<input type="number" min="0" value={form.number_of_child} onChange={(e) => setForm({ ...form, number_of_child: e.target.value })} />
				</label>
			</div>

			<div className="ptr-form__row">
				<label className="ptr-form__field">
					<span className="ptr-form__label">Your name</span>
					<input type="text" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value, head_traveller_name: e.target.value })} required />
				</label>
				<label className="ptr-form__field">
					<span className="ptr-form__label">Email</span>
					<input type="email" value={form.email_address} onChange={(e) => setForm({ ...form, email_address: e.target.value })} required />
				</label>
			</div>

			<label className="ptr-form__field">
				<span className="ptr-form__label">Phone (with country code)</span>
				<input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
			</label>

			<label className="ptr-form__field">
				<span className="ptr-form__label">Special requirements</span>
				<textarea
					rows={4}
					value={form.special_requirements}
					onChange={(e) => setForm({ ...form, special_requirements: e.target.value })}
					placeholder="Pace, accommodation preferences, dietary needs, experience level…"
				/>
			</label>

			<button type="submit" className="ptr-form__cta" disabled={submitting || lookupLoading}>
				{submitting ? 'Sending…' : 'Submit Private Tour Request →'}
			</button>
		</form>
	);
}

export default PrivateTourRequestForm;
